import { isIdOfKind } from '@loctopus/shared';

/**
 * Ported from apps/scanner/src/lib/scanPayload.ts (identical) - handles the three plausible
 * things a ticket QR might encode: a bare ticket ID, a ticket URL with the ID as the last path
 * segment, or a JSON payload with a `ticketId`/`id` field - and otherwise falls back to the raw
 * scanned text so the server's own error message (not a client-side guess) is what staff see.
 */
export function extractTicketId(rawPayload: string): string {
  const trimmed = rawPayload.trim();

  // REAL BUG FOUND AND FIXED: a real ticket's QR encodes `{ticketId}.{signature}`
  // (packages/tickets/src/qr.ts's `signTicketPayload`), e.g.
  // "tix_01m6....ngj7qf.TBajMAttWqW4...". The prefix check below (`isIdOfKind`) only verifies the
  // string STARTS WITH "tix_" - which the whole signed payload also does - so it used to return
  // the signature-and-all string as if it were already a clean id, and every real scan of an
  // actual issued ticket failed with "not found" (confirmed live: the exact `id.signature` string
  // appeared verbatim in the 404 message). Split off the signature FIRST, before the loose prefix
  // check gets a chance to accept the whole thing.
  const dotIndex = trimmed.indexOf('.');
  if (dotIndex > 0 && isIdOfKind(trimmed.slice(0, dotIndex), 'ticket')) {
    return trimmed.slice(0, dotIndex);
  }

  if (isIdOfKind(trimmed, 'ticket')) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed);
    const segments = url.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last && isIdOfKind(last, 'ticket')) {
      return last;
    }
  } catch {
    // not a URL, fall through
  }

  try {
    const parsed = JSON.parse(trimmed) as { ticketId?: string; id?: string };
    const candidate = parsed.ticketId ?? parsed.id;
    if (candidate) return candidate;
  } catch {
    // not JSON, fall through
  }

  return trimmed;
}
